import React from "react";
import { AccessorFn, ColumnDef, DeepKeys } from "@tanstack/react-table";
import { TableCell } from "@/components/ui/table";
import { CellHoverClassName } from "@/components/List/ListTable/TableBody/Rows/TableDataRow";
import { AppLink, AppLinkProps } from "@/components/AppLink";
import { RouteKeys } from "@/types/router";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

type TooltipProps = React.ComponentProps<typeof TooltipContent>;

type LinkColumnProps<T, K extends RouteKeys = RouteKeys> = Omit<ColumnDef<T>, "accessorKey"> & {
	align?: "left" | "center" | "right";
	className?: string;
	linkClassName?: string;
	accessorKey?: DeepKeys<T>;
	accessorFn?: AccessorFn<T>; // added support
	linkText: string | ((row: T) => React.ReactNode);
	link: ((row: T) => AppLinkProps<K>) | AppLinkProps<K>;
	tooltipText?: string | ((row: T) => string);
	tooltipProps?: TooltipProps;
};

export const LinkColumn = <T, K extends RouteKeys = RouteKeys>(props: LinkColumnProps<T, K>): ColumnDef<T> => {
	const {
		accessorKey,
		accessorFn,
		linkText,
		header,
		align = "left",
		className = "",
		linkClassName = "",
		meta,
		link,
		tooltipText,
		tooltipProps,
		...rest
	} = props;

	const mergedClassName = meta?.cellClassName || className;

	return {
		accessorKey: accessorKey as string,
		header,
		meta: { wrapWithTableCell: false, cellClassName: mergedClassName, ...meta },
		cell: ({ row }) => {
			const text = typeof linkText === "function" ? linkText(row.original) : linkText;
			const alignment = { left: "text-left", center: "text-center", right: "text-right" }[align];
			const linkProps = typeof link === "function" ? link(row.original) : link;
			const tooltip = typeof tooltipText === "function" ? tooltipText(row.original) : tooltipText;

			return (
				<TableCell className={`${CellHoverClassName} ${alignment} ${mergedClassName}`}>
					{tooltipText ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<AppLink
									{...linkProps}
									className={`text-primary hover:text-primary/80 hover:underline transition-colors ${linkClassName}`}
								>
									{text}
								</AppLink>
							</TooltipTrigger>
							<TooltipContent {...tooltipProps}>{tooltip}</TooltipContent>
						</Tooltip>
					) : (
						<AppLink
							{...linkProps}
							className={`text-primary hover:text-primary/80 hover:underline transition-colors ${linkClassName}`}
						>
							{text}
						</AppLink>
					)}
				</TableCell>
			);
		},
		...rest,
	};
};
